import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Building2, Users, Layers, Shield, History, Save, UserPlus, KeyRound, Package } from "lucide-react";
import { fmtDateTime } from "@/lib/dateFormat";

const SCOPES = [
  { v: "self", l: "SELF — Only own orders" },
  { v: "team", l: "TEAM — Ordered_by_team snapshot match" },
  { v: "company", l: "COMPANY — All in company" },
];

export default function AdminClientDetailPage() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [co, us, tm, rl, ad] = await Promise.all([
        apiClient.get(`/admin/clients/${companyId}`),
        apiClient.get(`/admin/clients/${companyId}/users`),
        apiClient.get(`/admin/clients/${companyId}/teams`),
        apiClient.get(`/admin/clients/roles`),
        apiClient.get(`/admin/clients/${companyId}/audit`),
      ]);
      setCompany(co.data); setUsers(us.data); setTeams(tm.data);
      setRoles(rl.data.roles); setAudit(ad.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  }, [companyId]);

  // Initial mount only — subsequent refetches (Save / Create / Deactivate)
  // run silently so the active tab stays mounted and selected.
  useEffect(() => { load(); }, [load]);

  if (loading || !company) {
    return <div className="p-8 text-slate-500" data-testid="admin-client-loading">Loading client…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-client-detail">
      <Link to="/admin/clients" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0A192F]" data-testid="admin-back">
        <ArrowLeft className="h-4 w-4" /> Back to clients
      </Link>
      <div>
        <div className="ntn-eyebrow text-slate-500">Client Account</div>
        <h1 className="font-serif text-3xl text-[#0A192F]" data-testid="admin-company-name">{company.legal_name || company.name}</h1>
        <div className="text-sm text-slate-500 mt-1">
          Status: <StatusBadge status={company.status || "active"} /> · {users.length} user{users.length===1?"":"s"} · {teams.length} team{teams.length===1?"":"s"}
        </div>
      </div>

      <Tabs defaultValue="company" className="w-full">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="company" data-testid="tab-company"><Building2 className="h-4 w-4 mr-1.5" />Company</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><Users className="h-4 w-4 mr-1.5" />Users</TabsTrigger>
          <TabsTrigger value="teams" data-testid="tab-teams"><Layers className="h-4 w-4 mr-1.5" />Teams</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products"><Package className="h-4 w-4 mr-1.5" />Products</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles"><Shield className="h-4 w-4 mr-1.5" />Roles</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><History className="h-4 w-4 mr-1.5" />Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="company"><CompanyTab company={company} onSaved={load} /></TabsContent>
        <TabsContent value="users"><UsersTab companyId={companyId} users={users} teams={teams} roles={roles} onChanged={load} /></TabsContent>
        <TabsContent value="teams"><TeamsTab companyId={companyId} teams={teams} onChanged={load} /></TabsContent>
        <TabsContent value="products"><ProductsTab companyId={companyId} company={company} onSaved={load} /></TabsContent>
        <TabsContent value="roles"><RolesTab roles={roles} /></TabsContent>
        <TabsContent value="audit"><AuditTab audit={audit} /></TabsContent>
      </Tabs>
    </div>
  );
}

function StatusBadge({ status }) {
  const tones = { active: "bg-emerald-100 text-emerald-800", suspended: "bg-amber-100 text-amber-800", closed: "bg-slate-200 text-slate-700", inactive: "bg-slate-200 text-slate-700" };
  return <Badge className={tones[status] || "bg-slate-100"}>{status}</Badge>;
}

// ─────────── Company tab ───────────
function CompanyTab({ company, onSaved }) {
  const [form, setForm] = useState({ ...company });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(
        ([k, v]) => v !== company[k] && ["legal_name","display_name","primary_phone","address_line_1","address_line_2","city","state","postal_code","country","primary_contact_email","screening_fee_cap_usd","account_number","application_link_url"].includes(k)
      ));
      if (!Object.keys(payload).length) { toast.info("No changes"); setBusy(false); return; }
      await apiClient.patch(`/admin/clients/${company.id}`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  const changeStatus = async (status) => {
    if (!confirm(`Change company status to "${status}"?`)) return;
    try { await apiClient.post(`/admin/clients/${company.id}/status`, { status }); toast.success("Status updated"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const F = (label, key, type = "text") => (
    <div><Label className="text-xs">{label}</Label><Input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: type === "number" ? parseFloat(e.target.value) || null : e.target.value })} data-testid={`co-${key}`} /></div>
  );
  return (
    <Card className="p-6 mt-4 space-y-5">
      <div className="grid grid-cols-2 gap-4">
        {F("Legal name", "legal_name")}
        {F("Display name", "display_name")}
        {F("Primary phone", "primary_phone")}
        {F("Primary contact email", "primary_contact_email")}
        {F("Address line 1", "address_line_1")}
        {F("Address line 2", "address_line_2")}
        {F("City", "city")}
        {F("State", "state")}
        {F("Postal code", "postal_code")}
        {F("Country", "country")}
        {F("Screening fee cap (USD)", "screening_fee_cap_usd", "number")}
        {F("Account number", "account_number")}
        {F("Application link URL", "application_link_url")}
      </div>
      <div className="flex justify-between items-center pt-4 border-t">
        <div className="flex gap-2">
          {["active","suspended","closed"].map((s) => (
            <Button key={s} variant={company.status === s ? "default" : "outline"} onClick={() => changeStatus(s)} data-testid={`co-status-${s}`}>{s}</Button>
          ))}
        </div>
        <Button onClick={save} disabled={busy} data-testid="co-save"><Save className="h-4 w-4 mr-1.5" />{busy ? "Saving…" : "Save"}</Button>
      </div>
    </Card>
  );
}

// ─────────── Users tab ───────────
function UsersTab({ companyId, users, teams, roles, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between">
        <div className="text-sm text-slate-500">{users.length} user{users.length===1?"":"s"}</div>
        <Button onClick={() => setAddOpen(true)} data-testid="user-add"><UserPlus className="h-4 w-4 mr-1.5" />Add user</Button>
      </div>
      <Card className="divide-y">
        {users.map((u) => (
          <div key={u.id} className="p-4 flex items-center justify-between hover:bg-slate-50 cursor-pointer" onClick={() => setDetailUser(u)} data-testid={`user-row-${u.id}`}>
            <div>
              <div className="font-medium">{u.name || u.email}</div>
              <div className="text-xs text-slate-500">{u.email} · {roles.find(r=>r.id===u.role_id)?.name || u.role_id || "no role"} · scope={u.report_scope || "—"}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-200" title="Email verification code required at every sign-in" data-testid={`user-mfa-${u.id}`}>MFA: Email</span>
              <StatusBadge status={u.status || "active"} />
            </div>
          </div>
        ))}
      </Card>
      {addOpen && <AddUserModal companyId={companyId} teams={teams} roles={roles} onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); onChanged(); }} />}
      {detailUser && <UserDetailModal companyId={companyId} user={detailUser} teams={teams} roles={roles} onClose={() => setDetailUser(null)} onChanged={() => { setDetailUser(null); onChanged(); }} />}
    </div>
  );
}

function AddUserModal({ companyId, teams, roles, onClose, onCreated }) {
  const [f, setF] = useState({ email: "", first_name: "", last_name: "", phone: "", job_title: "", role_id: "role_report_viewer", report_scope: "self", primary_team_id: "", initial_password: "" });
  const submit = async () => {
    try {
      await apiClient.post(`/admin/clients/${companyId}/users`, { ...f, team_ids: f.primary_team_id ? [f.primary_team_id] : [] });
      toast.success("User created"); onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Create failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="max-w-lg w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="add-user-modal">
        <h3 className="font-serif text-xl">Add client user</h3>
        {[["email","Email"],["first_name","First name"],["last_name","Last name"],["phone","Phone"],["job_title","Job title"],["initial_password","Initial password (≥10 chars)"]].map(([k,l]) => (
          <div key={k}><Label className="text-xs">{l}</Label><Input value={f[k]} onChange={(e)=>setF({...f,[k]:e.target.value})} data-testid={`add-${k}`} /></div>
        ))}
        <div><Label className="text-xs">Role</Label>
          <Select value={f.role_id} onValueChange={(v)=>setF({...f, role_id:v})}>
            <SelectTrigger data-testid="add-role"><SelectValue /></SelectTrigger>
            <SelectContent>{roles.map((r)=><SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Report scope</Label>
          <Select value={f.report_scope} onValueChange={(v)=>setF({...f, report_scope:v})}>
            <SelectTrigger data-testid="add-scope"><SelectValue /></SelectTrigger>
            <SelectContent>{SCOPES.map((s)=><SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {teams.length > 0 && <div><Label className="text-xs">Primary team</Label>
          <Select value={f.primary_team_id} onValueChange={(v)=>setF({...f, primary_team_id:v})}>
            <SelectTrigger data-testid="add-team"><SelectValue placeholder="None"/></SelectTrigger>
            <SelectContent>{teams.map((t)=><SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>}
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="add-submit">Create</Button>
        </div>
      </Card>
    </div>
  );
}

function UserDetailModal({ companyId, user, teams, roles, onClose, onChanged }) {
  const [eff, setEff] = useState(null);
  const [form, setForm] = useState({ role_id: user.role_id, report_scope: user.report_scope, primary_team_id: user.primary_team_id, status: user.status || "active",
                                       grants: (user.permission_overrides?.grants || []).join(","),
                                       revokes: (user.permission_overrides?.revokes || []).join(",") });
  useEffect(() => {
    apiClient.get(`/admin/clients/${companyId}/users/${user.id}/effective`).then(r => setEff(r.data));
  }, [companyId, user.id]);
  const save = async () => {
    try {
      const payload = {
        role_id: form.role_id, report_scope: form.report_scope, primary_team_id: form.primary_team_id || null,
        team_ids: form.primary_team_id ? [form.primary_team_id] : [],
        permission_overrides: {
          grants: form.grants.split(",").map(s=>s.trim()).filter(Boolean),
          revokes: form.revokes.split(",").map(s=>s.trim()).filter(Boolean),
        },
        status: form.status,
      };
      await apiClient.patch(`/admin/clients/${companyId}/users/${user.id}`, payload);
      toast.success("Saved"); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const resetPwd = async () => {
    if (!confirm("Reset this user's password? A temporary password will be generated once.")) return;
    try {
      const r = await apiClient.post(`/admin/clients/${companyId}/users/${user.id}/reset-password`);
      window.prompt("Copy the temporary password NOW — this is only shown once:", r.data.temp_password);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
  };
  const deactivate = async () => {
    if (!confirm(`Deactivate ${user.email}? They will be immediately signed out and blocked.`)) return;
    try { await apiClient.patch(`/admin/clients/${companyId}/users/${user.id}`, { status: "inactive" }); toast.success("Deactivated"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="max-w-3xl w-full max-h-[90vh] overflow-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="user-detail-modal">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-serif text-xl">{user.name || user.email}</h3>
            <div className="text-sm text-slate-500">{user.email}</div>
          </div>
          <StatusBadge status={user.status || "active"} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs">Role</Label>
            <Select value={form.role_id} onValueChange={(v)=>setForm({...form, role_id:v})}>
              <SelectTrigger data-testid="ud-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r)=><SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Report scope</Label>
            <Select value={form.report_scope} onValueChange={(v)=>setForm({...form, report_scope:v})}>
              <SelectTrigger data-testid="ud-scope"><SelectValue /></SelectTrigger>
              <SelectContent>{SCOPES.map((s)=><SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Primary team</Label>
            <Select value={form.primary_team_id || ""} onValueChange={(v)=>setForm({...form, primary_team_id:v})}>
              <SelectTrigger data-testid="ud-team"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>{teams.map((t)=><SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Status</Label>
            <Select value={form.status} onValueChange={(v)=>setForm({...form, status:v})}>
              <SelectTrigger data-testid="ud-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">active</SelectItem><SelectItem value="inactive">inactive</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label className="text-xs">Explicit grants (comma-sep permissions)</Label>
            <Input value={form.grants} onChange={(e)=>setForm({...form, grants:e.target.value})} data-testid="ud-grants" placeholder="e.g. reports.download" />
          </div>
          <div className="col-span-2"><Label className="text-xs">Explicit revokes (comma-sep permissions — win over grants)</Label>
            <Input value={form.revokes} onChange={(e)=>setForm({...form, revokes:e.target.value})} data-testid="ud-revokes" />
          </div>
        </div>

        {eff && (
          <Card className="p-4 bg-slate-50 border-slate-200" data-testid="effective-panel">
            <div className="ntn-eyebrow text-slate-600 mb-2">Effective permissions</div>
            <div className="text-xs space-y-1.5 font-mono">
              <div>Base role: <strong>{eff.role_name || eff.role_id || "—"}</strong></div>
              <div>+ grants: {eff.grants.length ? eff.grants.join(", ") : "—"}</div>
              <div>− revokes: {eff.revokes.length ? eff.revokes.join(", ") : "—"}</div>
              <div className="pt-2 border-t border-slate-300">= <strong>{eff.effective_permissions.join(", ") || "(none)"}</strong></div>
              <div>Report scope: <strong>{eff.report_scope.toUpperCase()}</strong></div>
              <div>Status: <strong>{eff.status}</strong></div>
            </div>
            <div className="text-[10px] text-slate-500 mt-2 italic">Computed by backend authz.effective_permissions() — never recomputed in the UI.</div>
          </Card>
        )}

        {/* Phase G (2026-02): Subscriber Responsibilities / FCRA acknowledgment
            status. Read-only for NTN staff — property managers cannot modify. */}
        {user.subscriber_ack_status && (
          <Card className="p-4 bg-slate-50 border-slate-200" data-testid="subscriber-ack-panel">
            <div className="ntn-eyebrow text-slate-600 mb-2">Subscriber Acknowledgment</div>
            <div className="text-xs space-y-1.5 font-mono">
              <div>
                Status:{" "}
                <strong
                  className={user.subscriber_ack_status.is_current_version ? "text-emerald-700" : "text-amber-700"}
                  data-testid="ack-status-label"
                >
                  {user.subscriber_ack_status.is_current_version ? "Accepted" : "Pending"}
                </strong>
              </div>
              <div>Version accepted: <strong data-testid="ack-status-version">{user.subscriber_ack_status.version || "—"}</strong></div>
              <div>Accepted at: <strong>{user.subscriber_ack_status.accepted_at ? fmtDateTime(user.subscriber_ack_status.accepted_at) : "—"}</strong></div>
              <div>Current version: <strong>{user.subscriber_ack_status.current_version}</strong></div>
              <div>Matches current: <strong>{user.subscriber_ack_status.is_current_version ? "Yes" : "No"}</strong></div>
            </div>
            <div className="text-[10px] text-slate-500 mt-2 italic">Read-only. PM users cannot modify this record.</div>
          </Card>
        )}

        <div className="flex justify-between items-center pt-3 border-t">
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetPwd} data-testid="ud-reset-pwd"><KeyRound className="h-4 w-4 mr-1.5" />Reset password</Button>
            {form.status === "active" && <Button variant="outline" className="text-red-600" onClick={deactivate} data-testid="ud-deactivate">Deactivate</Button>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={save} data-testid="ud-save">Save changes</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ─────────── Teams tab ───────────
function TeamsTab({ companyId, teams, onChanged }) {
  const [name, setName] = useState("");
  const create = async () => {
    if (!name.trim()) return;
    try { await apiClient.post(`/admin/clients/${companyId}/teams`, { name: name.trim() }); toast.success("Team created"); setName(""); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Create failed"); }
  };
  const rename = async (t) => {
    const nn = prompt("New team name:", t.name);
    if (!nn || nn === t.name) return;
    try { await apiClient.patch(`/admin/clients/${companyId}/teams/${t.id}`, { name: nn }); toast.success("Renamed"); onChanged(); }
    catch (e) { toast.error("Failed"); }
  };
  const toggleStatus = async (t) => {
    const next = t.status === "active" ? "inactive" : "active";
    if (!confirm(`Set ${t.name} to ${next}?`)) return;
    try { await apiClient.patch(`/admin/clients/${companyId}/teams/${t.id}`, { status: next }); onChanged(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="mt-4 space-y-4">
      <div className="flex gap-2">
        <Input placeholder="New team name" value={name} onChange={(e)=>setName(e.target.value)} data-testid="team-name" />
        <Button onClick={create} data-testid="team-create">Create team</Button>
      </div>
      <Card className="divide-y">
        {teams.map((t) => (
          <div key={t.id} className="p-3 flex items-center justify-between" data-testid={`team-row-${t.id}`}>
            <div><span className="font-medium">{t.name}</span> <span className="text-xs text-slate-500 ml-2">{t.id}</span></div>
            <div className="flex gap-2 items-center">
              <StatusBadge status={t.status} />
              <Button variant="ghost" size="sm" onClick={() => rename(t)}>Rename</Button>
              <Button variant="ghost" size="sm" onClick={() => toggleStatus(t)}>{t.status === "active" ? "Deactivate" : "Reactivate"}</Button>
            </div>
          </div>
        ))}
        {teams.length === 0 && <div className="p-6 text-slate-500 text-sm">No teams yet.</div>}
      </Card>
    </div>
  );
}

// ─────────── Roles tab ───────────
function RolesTab({ roles }) {
  return (
    <div className="mt-4 space-y-3">
      {roles.map((r) => (
        <Card key={r.id} className="p-4" data-testid={`role-${r.id}`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="font-medium">{r.name} {r.is_system && <Badge className="ml-2 bg-slate-100 text-slate-600">system</Badge>}</div>
              <div className="text-sm text-slate-500 mt-1">{r.description}</div>
            </div>
            <Badge className="bg-blue-50 text-blue-700">scope: {r.default_report_scope}</Badge>
          </div>
          <div className="text-xs font-mono mt-3 text-slate-600">{(r.permissions || []).join(" · ") || "(none)"}</div>
        </Card>
      ))}
    </div>
  );
}

// ─────────── Audit tab ───────────
function AuditTab({ audit }) {
  return (
    <Card className="mt-4 divide-y" data-testid="audit-list">
      {audit.length === 0 && <div className="p-6 text-slate-500 text-sm">No audit events yet for this account.</div>}
      {audit.map((e, i) => (
        <div key={i} className="p-3 text-sm">
          <div className="flex justify-between">
            <span className="font-mono text-xs text-slate-500">{e.action || e.type}</span>
            <span className="text-xs text-slate-400">{e.created_at?.slice(0, 19).replace("T", " ")}</span>
          </div>
          <div className="text-xs text-slate-600 mt-1">
            by <strong>{e.actor_name || e.actor_id}</strong> · target {e.target_type}/{e.target_id?.slice(0, 12)}
          </div>
          {e.metadata && Object.keys(e.metadata).length > 0 && (
            <details className="mt-1"><summary className="text-xs text-slate-400 cursor-pointer">details</summary>
              <pre className="text-[10px] text-slate-500 mt-1 overflow-x-auto">{JSON.stringify(e.metadata, null, 2)}</pre>
            </details>
          )}
        </div>
      ))}
    </Card>
  );
}


// ─────────── Products tab (Phase E.2) ───────────
function ProductsTab({ companyId, company, onSaved }) {
  const [catalog, setCatalog] = useState([]);
  const [allowed, setAllowed] = useState(new Set(company.allowed_products || []));
  const [defaults, setDefaults] = useState(new Set(company.default_products || []));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/admin/clients/product-catalog");
        setCatalog(data.products || []);
      } catch (e) {
        toast.error("Could not load product catalog.");
      }
    })();
  }, []);

  useEffect(() => {
    setAllowed(new Set(company.allowed_products || []));
    setDefaults(new Set(company.default_products || []));
  }, [company.allowed_products, company.default_products]);

  const toggleAllowed = (key) => {
    const next = new Set(allowed);
    if (next.has(key)) {
      next.delete(key);
      // Tightening the allowlist prunes the default set too.
      const nd = new Set(defaults); nd.delete(key); setDefaults(nd);
    } else next.add(key);
    setAllowed(next);
  };

  const toggleDefault = (key) => {
    if (!allowed.has(key)) return;   // can only default what's allowed
    const next = new Set(defaults);
    if (next.has(key)) next.delete(key); else next.add(key);
    setDefaults(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.patch(`/admin/clients/${companyId}/products`, {
        allowed_products: Array.from(allowed),
        default_products: Array.from(defaults),
      });
      if (data.changed === false) toast.info("No changes");
      else toast.success("Product catalog saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const dirty =
    !setsEqual(allowed, new Set(company.allowed_products || [])) ||
    !setsEqual(defaults, new Set(company.default_products || []));

  return (
    <Card className="p-6 mt-4 space-y-4" data-testid="products-tab">
      <div>
        <h2 className="font-serif text-xl text-[#0A192F]">Available Reports</h2>
        <p className="text-xs text-slate-500 mt-1">
          Control which products this company may order and which are preselected
          on a new request. Products outside the allowlist are hidden from the
          client's New Request screen <em>and</em> rejected server-side (403).
        </p>
      </div>

      <div className="border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="products-grid">
        <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-2 bg-slate-50 text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">
          <div>Product</div><div>Allowed</div><div>Default</div>
        </div>
        {catalog.map((p) => {
          const isAllowed = allowed.has(p.key);
          const isDefault = defaults.has(p.key);
          return (
            <div key={p.key} className="grid grid-cols-[1fr_auto_auto] gap-4 items-center px-4 py-3" data-testid={`product-row-${p.key}`}>
              <div>
                <div className="text-sm text-slate-900">{p.label}</div>
                <div className="text-[10px] font-mono text-slate-400">{p.key}</div>
              </div>
              <input
                type="checkbox"
                checked={isAllowed}
                onChange={() => toggleAllowed(p.key)}
                className="h-5 w-5 accent-[#0A192F]"
                data-testid={`product-allowed-${p.key}`}
              />
              <input
                type="checkbox"
                checked={isDefault}
                disabled={!isAllowed}
                onChange={() => toggleDefault(p.key)}
                className={`h-5 w-5 accent-emerald-700 ${!isAllowed ? "opacity-30" : ""}`}
                data-testid={`product-default-${p.key}`}
              />
            </div>
          );
        })}
      </div>

      <div className="text-[11px] text-slate-500 italic">
        Preselected default products load automatically on the client&apos;s New Request screen.
        A default can never be outside the allowlist — unchecking a product removes it from both.
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy || !dirty}
                className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-11 px-5"
                data-testid="products-save">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save catalog"}
        </Button>
      </div>
    </Card>
  );
}

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}
